import React, { useRef, useState } from "react";
import { Sparkles, Type } from "lucide-react";
import { api, resolveImg } from "../lib/api";
import { toast } from "sonner";

/**
 * BlockImageOverlayEditor
 * ------------------------
 * Renders the uploaded block image with a draggable text-anchor pin.
 * Lets the admin choose if the image already has baked-in text (in which
 * case we don't render any overlay), and offers an "AI suggest position"
 * button that calls Gemini vision to find the largest empty area.
 *
 * Props:
 *   block       — the LongDescBlock object ({image_url, heading, body, image_has_text, text_x, text_y, ...})
 *   onChange    — (patch) => void  — partial update to merge into the block
 */
export default function BlockImageOverlayEditor({ block, onChange }) {
  const containerRef = useRef(null);
  const [draggingPin, setDraggingPin] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  if (!block?.image_url) return null;

  const x = typeof block.text_x === "number" ? block.text_x : 50;
  const y = typeof block.text_y === "number" ? block.text_y : 50;

  // Drag handlers — convert mouse position to percent within the preview box
  const updateFromEvent = (e) => {
    const box = containerRef.current?.getBoundingClientRect();
    if (!box) return;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;
    const px = ((clientX - box.left) / box.width) * 100;
    const py = ((clientY - box.top) / box.height) * 100;
    onChange({
      text_x: Math.max(2, Math.min(98, Math.round(px * 10) / 10)),
      text_y: Math.max(2, Math.min(98, Math.round(py * 10) / 10)),
    });
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    setDraggingPin(true);
    updateFromEvent(e);
    const move = (mv) => updateFromEvent(mv);
    const up = () => {
      setDraggingPin(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const askAI = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    const t = toast.loading("Analyzing image…");
    try {
      const { data } = await api.post("/admin/blocks/suggest-position", {
        image_url: block.image_url,
      });
      onChange({ text_x: data.x, text_y: data.y });
      toast.success(data.reason || `Best spot: ${data.x}%, ${data.y}%`, { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI position failed", { id: t });
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-2 mt-2">
      <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-slate-700 cursor-pointer">
        <input
          type="checkbox"
          checked={!!block.image_has_text}
          onChange={(e) => onChange({ image_has_text: e.target.checked })}
          data-testid="block-has-text-toggle"
          className="w-4 h-4 accent-electric"
        />
        <Type size={12} />
        Image already has text baked in
      </label>

      {!block.image_has_text && (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Text anchor — drag the pin to position the heading
          </div>
          <div
            ref={containerRef}
            data-testid="block-position-canvas"
            onPointerDown={handlePointerDown}
            className="relative w-full aspect-[4/3] bg-secondary rounded-lg overflow-hidden border border-black/10 cursor-crosshair select-none"
            style={{ touchAction: "none" }}
          >
            <img
              src={resolveImg(block.image_url)}
              alt=""
              draggable={false}
              className="w-full h-full object-cover pointer-events-none"
            />
            {/* Faux preview of the text at the pin location */}
            <div
              className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none px-2 py-1 bg-black/60 backdrop-blur-sm rounded text-white text-[10px] font-display font-bold uppercase tracking-wider whitespace-nowrap"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              {block.heading || "Heading here"}
            </div>
            {/* The draggable pin */}
            <div
              className={`absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-electric border-2 border-white shadow-lg ${
                draggingPin ? "scale-125" : ""
              } transition-transform`}
              style={{ left: `${x}%`, top: `${y}%` }}
              data-testid="block-position-pin"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] font-mono text-slate-500">
              x: {x.toFixed(1)}% · y: {y.toFixed(1)}%
            </div>
            <button
              type="button"
              onClick={askAI}
              disabled={aiBusy}
              data-testid="block-position-ai"
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-ink text-white hover:bg-electric text-[10px] font-mono uppercase tracking-widest disabled:opacity-50"
            >
              <Sparkles size={11} />
              {aiBusy ? "Thinking…" : "AI find best spot"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
